import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { ApiErrorResponse } from '../common/types';

export interface ErrorState {
  // Global error state
  globalError: ApiErrorResponse | null;

  // Loading states
  globalLoading: boolean;
  loadingStates: Record<string, boolean>;

  // Toast/notification state
  toasts: Toast[];

  // Form validation errors
  formErrors: Record<string, Record<string, string>>;
}

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  persistent?: boolean;
  timestamp: number;
}

export interface ErrorActions {
  // Global error actions
  setGlobalError: (error: ApiErrorResponse | null) => void;
  clearGlobalError: () => void;

  // Loading actions
  setGlobalLoading: (loading: boolean) => void;
  setLoadingState: (key: string, loading: boolean) => void;
  clearLoadingState: (key: string) => void;

  // Toast actions
  addToast: (toast: Omit<Toast, 'id' | 'timestamp'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Form error actions
  setFormErrors: (formId: string, errors: Record<string, string>) => void;
  clearFormErrors: (formId: string) => void;
  setFieldError: (formId: string, field: string, error: string) => void;
  clearFieldError: (formId: string, field: string) => void;

  // Helper actions
  handleApiError: (error: unknown, context?: string) => void;
  createNetworkError: (message: string, error?: unknown) => ApiErrorResponse;
}

export type ErrorStore = ErrorState & ErrorActions;

const initialState: ErrorState = {
  globalError: null,
  globalLoading: false,
  loadingStates: {},
  toasts: [],
  formErrors: {},
};

export const useErrorStore = create<ErrorStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // Global error actions
      setGlobalError: (error) =>
        set({ globalError: error }, false, 'setGlobalError'),

      clearGlobalError: () =>
        set({ globalError: null }, false, 'clearGlobalError'),

      // Loading actions
      setGlobalLoading: (loading) =>
        set({ globalLoading: loading }, false, 'setGlobalLoading'),

      setLoadingState: (key, loading) =>
        set(
          (state) => ({
            loadingStates: {
              ...state.loadingStates,
              [key]: loading,
            },
          }),
          false,
          'setLoadingState',
        ),

      clearLoadingState: (key) =>
        set(
          (state) => {
            const { [key]: _, ...rest } = state.loadingStates;
            return { loadingStates: rest };
          },
          false,
          'clearLoadingState',
        ),

      // Toast actions
      addToast: (toast) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newToast: Toast = {
          ...toast,
          id,
          timestamp: Date.now(),
          duration: toast.duration ?? (toast.type === 'error' ? 6000 : 4000),
        };

        set(
          (state) => ({
            toasts: [...state.toasts, newToast],
          }),
          false,
          'addToast',
        );

        // Auto remove toast after duration (unless persistent)
        if (!toast.persistent) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }
      },

      removeToast: (id) =>
        set(
          (state) => ({
            toasts: state.toasts.filter((toast) => toast.id !== id),
          }),
          false,
          'removeToast',
        ),

      clearToasts: () => set({ toasts: [] }, false, 'clearToasts'),

      // Form error actions
      setFormErrors: (formId, errors) =>
        set(
          (state) => ({
            formErrors: {
              ...state.formErrors,
              [formId]: errors,
            },
          }),
          false,
          'setFormErrors',
        ),

      clearFormErrors: (formId) =>
        set(
          (state) => {
            const { [formId]: _, ...rest } = state.formErrors;
            return { formErrors: rest };
          },
          false,
          'clearFormErrors',
        ),

      setFieldError: (formId, field, error) =>
        set(
          (state) => ({
            formErrors: {
              ...state.formErrors,
              [formId]: {
                ...state.formErrors[formId],
                [field]: error,
              },
            },
          }),
          false,
          'setFieldError',
        ),

      clearFieldError: (formId, field) =>
        set(
          (state) => {
            const formErrs = state.formErrors[formId];
            if (!formErrs) return state;

            const { [field]: _, ...rest } = formErrs;
            return {
              formErrors: {
                ...state.formErrors,
                [formId]: rest,
              },
            };
          },
          false,
          'clearFieldError',
        ),

      // Helper actions
      createNetworkError: (message, error) => ({
        success: false as const,
        error: {
          code: 'NETWORK_ERROR',
          message,
          details:
            error instanceof Error
              ? error.message
              : 'Please check your connection and try again',
        },
        request_id: `client_${Date.now()}`,
        timestamp: new Date().toISOString(),
      }),

      handleApiError: (error, context = 'operation') => {
        let networkError: ApiErrorResponse;

        // Check if it's already an ApiErrorResponse
        if (error && typeof error === 'object' && 'success' in error) {
          networkError = error as ApiErrorResponse;
        } else {
          // Create a network error
          networkError = get().createNetworkError(
            `Failed to ${context}`,
            error,
          );
        }

        // Set global error and show toast
        get().setGlobalError(networkError);
        get().addToast({
          type: 'error',
          message: networkError.error.message,
        });
      },
    }),
    {
      name: 'error-store',
    },
  ),
);
